import { Capacitor } from '@capacitor/core';
import { publicBaseUrl } from '../utils/urls';

// حالة الاتصال: المتصفح يعرف إن انقطعت الشبكة تمامًا، لكنه يقول «متصل» أحيانًا والخادم لا يُصل إليه
// (شبكة ضعيفة أو واي فاي بلا إنترنت). لذلك نتتبّع أيضًا آخر محاولة فعلية للوصول إلى الخادم.
const PROBE_INTERVAL_MS = 20000;

let serverReachable = true;
let probeTimer = null;
const listeners = new Set();

// وضع التطبيق يختاره الموظف: «أوفلاين» يعمل كأن الإنترنت مفصول (كل فحص جديد على الجهاز،
// ولا طلب يخرج إلى الخادم)، و«أونلاين» لا يضيف فحوصات على الجهاز. الموقع أونلاين دائمًا.
const MODE_KEY = 'app_mode';

export function getAppMode() {
  if (!Capacitor.isNativePlatform()) return 'online';
  try {
    return localStorage.getItem(MODE_KEY) === 'offline' ? 'offline' : 'online';
  } catch {
    return 'online';
  }
}

export function isOfflineMode() {
  return getAppMode() === 'offline';
}

export function setAppMode(mode) {
  try {
    localStorage.setItem(MODE_KEY, mode === 'offline' ? 'offline' : 'online');
  } catch {
    // التخزين غير متاح: يبقى الوضع الافتراضي.
  }
  if (mode !== 'offline') probeServer();
  emit();
}

function browserOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

// لا يوجد اتصال فعلي بالخادم (بغض النظر عن الوضع المختار).
export function isDisconnected() {
  return browserOffline() || !serverReachable;
}

export function isOffline() {
  return isOfflineMode() || isDisconnected();
}

// الشبكة مقطوعة تمامًا حسب المتصفح: لا فائدة من انتظار مهلة الطلب.
export function isBrowserOffline() {
  return browserOffline();
}

function emit() {
  const offline = isOffline();
  listeners.forEach((listener) => listener(offline));
}

export function subscribeNetwork(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function probeServer(timeoutMs = 8000) {
  if (browserOffline() || isOfflineMode()) return false;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const response = await fetch(`${publicBaseUrl()}/health`, { cache: 'no-store', signal: controller?.signal });
    if (response.ok) markServerReachable(true);
    return response.ok;
  } catch {
    // ما زال الخادم غير متاح.
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// قبل عملية يطلبها الموظف (رفع الآن): نتحقق فورًا بدل انتظار الفحص الدوري.
export async function ensureServerReachable() {
  if (browserOffline() || isOfflineMode()) return false;
  if (serverReachable) return true;
  return probeServer();
}

export function markServerReachable(reachable) {
  if (serverReachable === reachable) return;
  serverReachable = reachable;

  if (!reachable && !probeTimer) {
    probeTimer = setInterval(probeServer, PROBE_INTERVAL_MS);
  }
  if (reachable && probeTimer) {
    clearInterval(probeTimer);
    probeTimer = null;
  }

  emit();
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    probeServer();
    emit();
  });
  window.addEventListener('offline', emit);
}
